"use client";

import Link from "next/link";
import Image from "next/image";
import {
  MessageCircle,
  Headphones,
  BookOpen,
  Mail,
  Star,
} from "lucide-react";
import certificateLogoContact from "@/assets/images/footer/certifications/certificate-logo-contact.svg";
import CmsFeedbackForm from "../CmsFeedbackForm/CmsFeedbackForm";
import { FEEDBACK_SOURCES } from "@/utils/feedback-form";

const SUPPORT_CARDS = [
  {
    icon: MessageCircle,
    title: <Link href="/book-demo">Request a Demo</Link>,
    description: "Talk to our solutions team",
  },
  {
    icon: Headphones,
    title: "Get Onboarding Help",
    description: "We'll guide you step by step",
  },
  {
    icon: BookOpen,
    title: "Ask About AI Features",
    description: "Learn what's possible with Kroolo AI",
  },
  {
    icon: Mail,
    title: "Email Us Directly",
    description: <a href="mailto:help@kroolo.com">help@kroolo.com</a>,
  },
];

const STATS = [
  { value: "< 2h", label: "Avg. response time" },
  { value: "98%", label: "Satisfaction score" },
  { value: "24/7", label: "Support available" },
];

type ContactSupportLayoutProps = {
  email?: string;
};

const ContactSupportLayout = ({
  email = "help@kroolo.com",
}: ContactSupportLayoutProps) => {
  return (
    <section className="min-h-screen bg-[#F9FAFB] pb-20 pt-24 xl:pb-24 xl:pt-[120px]">
      <div className="container max-w-[1288px]">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="space-y-6">
            <nav className="text-[14px] text-[#6B7280]">
              <Link href="/" className="hover:text-[#6049E7]">
                Home
              </Link>
              <span className="mx-2">/</span>
              <span className="text-[#202020]">Contact Us</span>
            </nav>
            <span className="inline-block rounded-full bg-[#EDE9FE] px-4 py-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#6049E7]">
              Contact Support
            </span>
            <h1 className="text-[32px] font-bold leading-tight text-[#202020] xl:text-[40px]">
              We&apos;re here to{" "}
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                help
              </span>{" "}
              you{" "}
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                succeed.
              </span>
            </h1>
            <p className="text-[18px] font-bold text-[#202020]">
              Real humans. Real answers. Real fast.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {STATS.map((stat, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-[#E5E7EB] bg-white p-4"
                >
                  <p className="text-[24px] font-bold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent xl:text-[28px]">
                    {stat.value}
                  </p>
                  <p className="text-[12px] font-normal text-[#6B7280]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#781E8F] to-[#422CBB] p-6">
              <div className="mb-4 flex items-center gap-2">
                <Star className="h-4 w-4 text-white" />
                <span className="text-[16px] font-semibold tracking-wide text-white">
                  How can we help you?
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SUPPORT_CARDS.map((card, index) => (
                  <div
                    key={index}
                    className="rounded-[4px] bg-[#6049E7] p-4 transition-colors hover:bg-[#553ED0]"
                  >
                    <card.icon className="mb-2 h-6 w-6 text-white" />
                    <p className="font-semibold text-white">{card.title}</p>
                    <p className="text-[14px] text-[#C4B5FD]">
                      {card.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-4">
              <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]">
                Enterprise-grade security
              </p>
              <div className="flex flex-wrap gap-2">
                <Image
                  src={certificateLogoContact}
                  alt="Certificate Logo Contact"
                  className="h-auto w-full max-w-[400px] object-contain"
                  draggable={false}
                />
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-32">
            <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="flex items-center justify-between bg-gradient-to-br from-[#781E8F] to-[#422CBB] px-6 py-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#22C55E] animate-pulse" />
                  <span className="text-[14px] font-medium text-white">
                    Our team is online
                  </span>
                </div>
                <span className="text-[14px] font-medium text-white">
                  Avg. reply: &lt; 2 hours
                </span>
              </div>
              <div className="space-y-5 p-6 xl:p-8">
                <div>
                  <h2 className="text-[24px] font-bold text-[#202020]">
                    Get in touch
                  </h2>
                  <p className="mt-1 text-[14px] text-[#6B7280]">
                    Fill in the form and we&apos;ll respond within 2 hours.
                  </p>
                </div>
                <CmsFeedbackForm
                  source={FEEDBACK_SOURCES.CONTACT}
                  submitLabel="Send Message"
                  footerNote={
                    <p className="text-center text-[14px] text-[#6B7280]">
                      Or email us directly at{" "}
                      <a
                        href={`mailto:${email}`}
                        className="font-semibold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent underline"
                      >
                        {email}
                      </a>
                    </p>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSupportLayout;
